import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MapPin, Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerSalesDistrict = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('customer_sales_district');
  const [viewItem, setViewItem] = useState(null);
  const [salesDistricts, setSalesDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentDistrict, setCurrentDistrict] = useState(null);
  const [formData, setFormData] = useState({
    sales_district_id: '',
    name: ''
  });

  useEffect(() => {
    loadSalesDistricts();
  }, []);

  const loadSalesDistricts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/customer-sales-districts`);
      setSalesDistricts(response.data);
    } catch (error) {
      console.error('Error loading sales districts:', error);
      toast.error('Failed to load sales districts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.sales_district_id || !formData.name) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      if (editMode) {
        await axios.put(
          `${BACKEND_URL}/api/customer-sales-districts/${currentDistrict.sales_district_id}`,
          { name: formData.name }
        );
        toast.success('Sales District updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/customer-sales-districts`, formData);
        toast.success('Sales District created successfully');
      }
      
      setShowDialog(false);
      resetForm();
      loadSalesDistricts();
    } catch (error) {
      console.error('Error saving sales district:', error);
      toast.error(error.response?.data?.detail || 'Failed to save sales district');
    }
  };

  const handleEdit = (district) => {
    setCurrentDistrict(district);
    setFormData({
      sales_district_id: district.sales_district_id,
      name: district.name
    });
    setEditMode(true);
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({ sales_district_id: '', name: '' });
    setCurrentDistrict(null);
    setEditMode(false);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    resetForm();
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <MapPin className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Sales Districts</h1>
                <p className="text-sm text-gray-600">Define customer sales districts for regional segmentation</p>
              </div>
            </div>
            {writable && (<Button
              onClick={() => setShowDialog(true)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Sales District
            </Button>)}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-orange-600 mb-3" />
              <p className="text-sm text-gray-600">Loading sales districts...</p>
            </div>
          ) : salesDistricts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <MapPin className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No sales districts yet</h3>
              <p className="text-sm text-gray-600 mb-4">Create your first sales district</p>
              {writable && (<Button onClick={() => setShowDialog(true)} className="bg-orange-600 hover:bg-orange-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Sales District
              </Button>)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">District ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {salesDistricts.map((district) => (
                    <tr key={district.sales_district_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{district.sales_district_id}</span>
                      </td>
                      <td className="px-6 py-4">{district.name}</td>
                      <td className="px-6 py-4 text-right">
                        <RecordActions doctype="customer_sales_district" onView={() => setViewItem(district)} onEdit={() => handleEdit(district)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create/Edit Dialog */}
        {showDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editMode ? 'Edit Sales District' : 'Add Sales District'}
                </h2>
                <button
                  onClick={handleDialogClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Sales District ID *</Label>
                  <Input
                    maxLength={10}
                    value={formData.sales_district_id}
                    onChange={(e) => setFormData({ ...formData, sales_district_id: e.target.value.toUpperCase() })}
                    placeholder="e.g., NORTH, SOUTH"
                    disabled={editMode}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 10 characters</p>
                </div>

                <div>
                  <Label>Name *</Label>
                  <Input
                    maxLength={100}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., North Region"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 100 characters</p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={handleDialogClose} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700">
                    {editMode ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default CustomerSalesDistrict;
